import type { ReactNode } from "react";
import { Box, ThemeProvider, Typography } from "@mui/material";
import { interfaceColors as colors } from "./interfaceColors";
import { gamePanelSx, gameSummarySx, gameTheme } from "./interfaceTheme";

export function GameShell({
  title,
  subtitle,
  category = "Arcade",
  maxWidth = 900,
  children
}: {
  title: string;
  subtitle: string;
  category?: string;
  maxWidth?: number;
  children: ReactNode;
}) {
  return (
    <ThemeProvider theme={gameTheme}>
      <Box
        component="section"
        aria-label={`${title} game`}
        className="game-shell"
        sx={{
          width: "100%",
          maxWidth,
          mx: "auto",
          my: { xs: 0, sm: 2 },
          p: { xs: 2, sm: 4 },
          color: colors.text,
          bgcolor: colors.background,
          backgroundImage: `radial-gradient(ellipse at 90% 0%, ${colors.atmosphere} 0%, transparent 55%)`,
          border: `1px solid ${colors.border}`,
          borderRadius: 3,
          minWidth: 0,
          boxSizing: "border-box",
          "& summary": gameSummarySx,
          "& details[open] > summary": {
            mb: 1,
            "&::after": { transform: "rotate(45deg)" }
          },
          "& .game-board-canvas": {
            display: "block",
            maxWidth: "100%",
            height: "auto",
            border: `1px solid ${colors.border}`,
            borderRadius: 2
          }
        }}
      >
        <Typography
          variant="overline"
          sx={{ letterSpacing: ".2em", color: "primary.light" }}
        >
          {title} / {category}
        </Typography>
        <Typography
          component="h1"
          variant="h4"
          sx={{ fontWeight: 900, mt: 1, overflowWrap: "anywhere" }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ color: "text.secondary", mt: 1, mb: 3, maxWidth: 650 }}
        >
          {subtitle}
        </Typography>
        <Box className="game-shell-panel" sx={gamePanelSx}>
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export function GameStats({
  items
}: {
  items: { label: string; value: ReactNode }[];
}) {
  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
        width: "100%",
        m: 0,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        bgcolor: colors.background,
        overflow: "hidden"
      }}
    >
      {items.map(({ label, value }) => (
        <Box
          key={label}
          sx={{
            p: { xs: 1.5, sm: 2 },
            "&:not(:last-child)": { borderRight: `1px solid ${colors.border}` }
          }}
        >
          <Typography
            component="dt"
            sx={{
              fontSize: 10,
              letterSpacing: ".15em",
              textTransform: "uppercase",
              color: "text.secondary"
            }}
          >
            {label}
          </Typography>
          <Typography
            component="dd"
            sx={{
              m: 0,
              mt: 0.5,
              color: "primary.light",
              fontSize: { xs: 22, sm: 26 },
              fontWeight: 700,
              fontFamily: "ui-monospace, monospace"
            }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
